/**
 * Generación de PDF para reportes NOM-035-STPS-2018.
 * Reutiliza el patrón de pdfReport: window.open + HTML + print().
 */

import { formatDateMexicoCity, formatDateDDMMYYYY, formatDateTimeMexicoCity } from '@/lib/dateUtils';
import type { Employee, Nom35Assessment } from '@/types';


function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getGuideSubtitle(guideType: Nom35Assessment['guideType']): string {
  if (guideType === 'NOM35_G1') return 'Guía de Referencia I: Acontecimientos Traumáticos Severos (ATS)';
  if (guideType === 'NOM35_G2') return 'Guía de Referencia II: Factores de Riesgo Psicosocial';
  if (guideType === 'NOM35_G3') return 'Guía de Referencia III: Entorno Organizacional';
  return 'Guía de Referencia I: Acontecimientos Traumáticos Severos (ATS)';
}

function getGuideLabel(guideType: Nom35Assessment['guideType']): string {
  if (guideType === 'NOM35_G1') return 'Guía de Referencia I (ATS)';
  if (guideType === 'NOM35_G2') return 'Guía de Referencia II';
  if (guideType === 'NOM35_G3') return 'Guía de Referencia III';
  return 'Guía de Referencia I (ATS)';
}

function getResultadoLabel(a: Nom35Assessment): string {
  if (a.guideType === 'NOM35_G1') {
    return a.requiresClinicalAttention === true ? 'Requiere Atención Clínica' : 'Sin Riesgo';
  }
  return a.resultLevel ?? '—';
}

const PSYTALENT_LOGO = '/isotipo.png';

function buildLogoHeader(origin: string, clientLogoUrl?: string | null): string {
  const psySrc = origin ? `${origin}${PSYTALENT_LOGO}` : PSYTALENT_LOGO;
  const clientSrc = clientLogoUrl ? clientLogoUrl.replace(/&/g, '&amp;') : '';
  if (clientLogoUrl) {
    return `
      <div style="display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1.5rem;">
        <img src="${psySrc}" alt="PsyTalent" width="48" height="48" style="object-fit: contain;" onerror="this.style.display='none'">
        <img src="${clientSrc}" alt="Logo empresa" style="max-width: 120px; max-height: 40px; object-fit: contain; margin-left: auto;" onerror="this.style.display='none'">
      </div>`;
  }
  return `
    <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;">
      <img src="${psySrc}" alt="PsyTalent" width="48" height="48" style="object-fit: contain;" onerror="this.style.display='none'">
    </div>`;
}

/**
 * Genera y descarga el PDF del Expediente Histórico completo de un empleado.
 * Incluye datos generales, dictamen actual destacado e historial de evaluaciones.
 */
export async function generateNom35ExpedientePdf(
  employee: Employee,
  assessments: Nom35Assessment[],
  clientLogoUrl?: string | null
): Promise<void> {
  const win = window.open('', '_blank');
  if (!win) {
    throw new Error('Permite ventanas emergentes para descargar el PDF.');
  }

  const fullName = `${employee.firstName} ${employee.lastName}`.trim();
  const hireDateRaw = employee.hireDate ?? employee.dateOfHire ?? '';
  const hireDateFormatted =
    hireDateRaw && !Number.isNaN(Date.parse(hireDateRaw))
      ? formatDateDDMMYYYY(hireDateRaw)
      : 'No especificada';
  const generatedAt = formatDateTimeMexicoCity(new Date());

  const latest = assessments[0];
  let dictamenHtml = '';
  if (latest) {
    const evalDate = latest.completedAt
      ? formatDateMexicoCity(latest.completedAt)
      : latest.createdAt
        ? formatDateMexicoCity(latest.createdAt)
        : '—';
    if (latest.guideType === 'NOM35_G1') {
      if (latest.requiresClinicalAttention === true) {
        dictamenHtml = `
        <div style="border: 2px solid #dc2626; background: #fef2f2; padding: 1.25rem 1.5rem; margin: 1rem 0; border-radius: 8px;">
          <p style="margin: 0 0 0.5rem; font-size: 0.875rem; color: #6b7280;">Fecha de evaluación actual: ${escapeHtml(evalDate)}</p>
          <p style="margin: 0; font-weight: 700; color: #b91c1c; font-size: 1.1rem;">
            DICTAMEN: El trabajador SÍ REQUIERE valoración clínica por exposición a acontecimientos traumáticos severos.
          </p>
        </div>`;
      } else {
        dictamenHtml = `
        <div style="border: 2px solid #16a34a; background: #f0fdf4; padding: 1.25rem 1.5rem; margin: 1rem 0; border-radius: 8px;">
          <p style="margin: 0 0 0.5rem; font-size: 0.875rem; color: #6b7280;">Fecha de evaluación actual: ${escapeHtml(evalDate)}</p>
          <p style="margin: 0; font-weight: 700; color: #15803d; font-size: 1.1rem;">
            DICTAMEN: El trabajador NO REQUIERE valoración clínica.
          </p>
        </div>`;
      }
    } else {
      dictamenHtml = `
      <div style="border: 2px solid #e5e7eb; background: #f9fafb; padding: 1.25rem 1.5rem; margin: 1rem 0; border-radius: 8px;">
        <p style="margin: 0 0 0.5rem; font-size: 0.875rem; color: #6b7280;">Fecha de evaluación actual: ${escapeHtml(evalDate)}</p>
        <p style="margin: 0; font-weight: 600; color: #374151;">
          Resultado: ${escapeHtml(latest.resultLevel ?? '—')} (Puntaje: ${latest.resultScore ?? '—'})
        </p>
      </div>`;
    }
  }

  const historyRows =
    assessments.length > 0
      ? assessments
          .map((a) => {
            const fecha = a.completedAt
              ? formatDateMexicoCity(a.completedAt)
              : a.createdAt
                ? formatDateMexicoCity(a.createdAt)
                : '—';
            const guia = getGuideLabel(a.guideType);
            const resultado = getResultadoLabel(a);
            return `<tr>
              <td style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb;">${escapeHtml(fecha)}</td>
              <td style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb;">${escapeHtml(guia)}</td>
              <td style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb;">${escapeHtml(resultado)}</td>
            </tr>`;
          })
          .join('')
      : '';

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const logoHeader = buildLogoHeader(origin, clientLogoUrl);

  win.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Expediente Histórico NOM-035 - ${escapeHtml(fullName)}</title>
    </head>
    <body style="font-family: system-ui, -apple-system, sans-serif; padding: 2rem; max-width: 800px; margin: 0 auto;">
      ${logoHeader}
      <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;">
        <div>
          <h1 style="margin: 0; font-size: 1.25rem; font-weight: 600;">Expediente Histórico - NOM-035-STPS-2018</h1>
          <p style="margin: 0.25rem 0 0; font-size: 0.9rem; color: #6b7280;">Documento para auditorías STPS</p>
        </div>
      </div>

      <div style="margin-bottom: 1.5rem; padding: 1rem; background: #f9fafb; border-radius: 6px;">
        <p style="margin: 0 0 0.5rem;"><strong>Nombre del trabajador:</strong> ${escapeHtml(fullName)}</p>
        <p style="margin: 0;"><strong>Fecha de contratación:</strong> ${escapeHtml(hireDateFormatted)}</p>
      </div>

      <h2 style="font-size: 1rem; font-weight: 600; margin: 1rem 0 0.5rem;">Dictamen Actual</h2>
      ${dictamenHtml || '<p style="color: #6b7280;">No hay evaluaciones completadas.</p>'}

      ${assessments.length > 0 ? `
      <h2 style="font-size: 1rem; font-weight: 600; margin: 1.5rem 0 0.5rem;">Historial de Evaluaciones</h2>
      <table style="width: 100%; border-collapse: collapse; font-size: 0.875rem;">
        <thead>
          <tr style="background: #f3f4f6;">
            <th style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb; text-align: left;">Fecha de Evaluación</th>
            <th style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb; text-align: left;">Guía Aplicada</th>
            <th style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb; text-align: left;">Resultado</th>
          </tr>
        </thead>
        <tbody>
          ${historyRows}
        </tbody>
      </table>
      ` : ''}

      <p style="font-size: 0.8rem; color: #9ca3af; margin-top: 2rem;">
        Documento generado para cumplimiento de la NOM-035-STPS-2018.
      </p>
      <p style="font-size: 0.75rem; color: #9ca3af;">
        Generado por PsyTalent · ${escapeHtml(generatedAt)} (Ciudad de México)
      </p>
    </body>
    </html>
  `);
  win.document.close();
  win.focus();
  win.print();
  win.onafterprint = () => win.close();
}

/**
 * @deprecated Use generateNom35ExpedientePdf(employee, assessments) para expediente histórico completo.
 * Genera PDF de una sola evaluación (compatibilidad).
 */
export async function generateNom35IndividualPdf(
  assessment: Nom35Assessment,
  clientLogoUrl?: string | null
): Promise<void> {
  const employee: Employee = {
    id: assessment.employeeId,
    companyId: assessment.companyId,
    firstName: assessment.employeeName.split(' ')[0] ?? '',
    lastName: assessment.employeeName.split(' ').slice(1).join(' ') ?? '',
    email: '',
    dateOfHire: '',
    isActive: true,
  };
  await generateNom35ExpedientePdf(employee, [assessment], clientLogoUrl);
}

/**
 * Genera y descarga el PDF global del centro de trabajo.
 */
export async function generateNom35GlobalPdf(
  assessments: Nom35Assessment[],
  clientLogoUrl?: string | null
): Promise<void> {
  const win = window.open('', '_blank');
  if (!win) {
    throw new Error('Permite ventanas emergentes para descargar el PDF.');
  }

  const totalCompleted = assessments.length;
  const totalClinicalAttention = assessments.filter((a) => a.requiresClinicalAttention === true).length;
  const generatedAt = formatDateTimeMexicoCity(new Date());

  const rows = assessments
    .map(
      (a) => {
        const fecha = a.completedAt
          ? formatDateMexicoCity(a.completedAt)
          : a.createdAt
            ? formatDateMexicoCity(a.createdAt)
            : '—';
        const resultado =
          a.guideType === 'NOM35_G1'
            ? a.requiresClinicalAttention === true
              ? 'Requiere Atención'
              : 'Sin Riesgo'
            : escapeHtml(a.resultLevel ?? '—');
        return `<tr>
          <td style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb;">${escapeHtml(a.employeeName)}</td>
          <td style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb;">${escapeHtml(fecha)}</td>
          <td style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb;">${resultado}</td>
        </tr>`;
      }
    )
    .join('');

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const logoHeader = buildLogoHeader(origin, clientLogoUrl);

  win.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Reporte Global NOM-035</title>
    </head>
    <body style="font-family: system-ui, -apple-system, sans-serif; padding: 2rem; max-width: 900px; margin: 0 auto;">
      ${logoHeader}
      <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;">
        <div>
          <h1 style="margin: 0; font-size: 1.25rem; font-weight: 600;">Informe de Resultados - NOM-035-STPS-2018</h1>
          <p style="margin: 0.25rem 0 0; font-size: 0.9rem; color: #6b7280;">Reporte Global de Resultados - Guía de Referencia I</p>
        </div>
      </div>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1.5rem;">
        <div style="padding: 1rem; background: #f9fafb; border-radius: 6px; border: 1px solid #e5e7eb;">
          <p style="margin: 0; font-size: 1.5rem; font-weight: 600;">${totalCompleted}</p>
          <p style="margin: 0.25rem 0 0; font-size: 0.875rem; color: #6b7280;">Total de evaluaciones completadas</p>
        </div>
        <div style="padding: 1rem; background: ${totalClinicalAttention > 0 ? '#fef2f2' : '#f9fafb'}; border-radius: 6px; border: 1px solid ${totalClinicalAttention > 0 ? '#fecaca' : '#e5e7eb'};">
          <p style="margin: 0; font-size: 1.5rem; font-weight: 600; color: ${totalClinicalAttention > 0 ? '#b91c1c' : 'inherit'};">${totalClinicalAttention}</p>
          <p style="margin: 0.25rem 0 0; font-size: 0.875rem; color: #6b7280;">Trabajadores que requieren atención clínica</p>
        </div>
      </div>

      <h2 style="font-size: 1rem; font-weight: 600; margin: 1rem 0 0.5rem;">Tabla de Resultados</h2>
      <table style="width: 100%; border-collapse: collapse; font-size: 0.875rem;">
        <thead>
          <tr style="background: #f3f4f6;">
            <th style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb; text-align: left;">Nombre del Trabajador</th>
            <th style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb; text-align: left;">Fecha de Evaluación</th>
            <th style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb; text-align: left;">Resultado</th>
          </tr>
        </thead>
        <tbody>
          ${rows || '<tr><td colspan="3" style="padding: 1rem; text-align: center; color: #6b7280;">No hay evaluaciones completadas.</td></tr>'}
        </tbody>
      </table>

      <p style="font-size: 0.8rem; color: #9ca3af; margin-top: 2rem;">
        Documento generado para cumplimiento de la NOM-035-STPS-2018.
      </p>
      <p style="font-size: 0.75rem; color: #9ca3af;">
        Generado por PsyTalent · ${escapeHtml(generatedAt)} (Ciudad de México)
      </p>
    </body>
    </html>
  `);
  win.document.close();
  win.focus();
  win.print();
  win.onafterprint = () => win.close();
}

export interface Nom35CampaignHistoricalPdfSummary {
  title: string;
  createdAt: string;
  closedAt?: string;
  status: string;
}

export interface Nom35CampaignHistoricalPdfStats {
  totalCompleted: number;
  /** Riesgo alto o muy alto en Guías II/III (G2/G3). */
  highRiskG2G3: number;
  /** Atención clínica según Guía I (ATS). */
  clinicalG1: number;
}

function getGuideShortLabel(guideType: Nom35Assessment['guideType']): string {
  if (guideType === 'NOM35_G1') return 'Guía I (ATS)';
  if (guideType === 'NOM35_G2') return 'Guía II';
  if (guideType === 'NOM35_G3') return 'Guía III';
  return '—';
}

function getCampaignRowResultLabel(a: Nom35Assessment): string {
  if (a.guideType === 'NOM35_G1') {
    return a.requiresClinicalAttention === true ? 'Requiere Atención' : 'Sin Riesgo';
  }
  return a.resultLevel ?? '—';
}

/**
 * PDF de resultados de una campaña NOM-035 cerrada o histórica (resumen + tabla individual).
 * Misma información que la pantalla de resultados anteriores.
 */
export async function generateNom35CampaignHistoricalPdf(
  summary: Nom35CampaignHistoricalPdfSummary,
  stats: Nom35CampaignHistoricalPdfStats,
  assessments: Nom35Assessment[],
  clientLogoUrl?: string | null
): Promise<void> {
  const win = window.open('', '_blank');
  if (!win) {
    throw new Error('Permite ventanas emergentes para descargar el PDF.');
  }

  const generatedAt = formatDateTimeMexicoCity(new Date());
  const inicio = formatDateMexicoCity(summary.createdAt);
  const cierre = summary.closedAt ? formatDateMexicoCity(summary.closedAt) : '—';

  const rows =
    assessments.length > 0
      ? assessments
          .map((a) => {
            const fecha = a.completedAt
              ? formatDateMexicoCity(a.completedAt)
              : a.createdAt
                ? formatDateMexicoCity(a.createdAt)
                : '—';
            const guia = getGuideShortLabel(a.guideType);
            const resultado = getCampaignRowResultLabel(a);
            return `<tr>
              <td style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb;">${escapeHtml(a.employeeName)}</td>
              <td style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb;">${escapeHtml(fecha)}</td>
              <td style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb;">${escapeHtml(guia)}</td>
              <td style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb;">${escapeHtml(resultado)}</td>
            </tr>`;
          })
          .join('')
      : '';

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const logoHeader = buildLogoHeader(origin, clientLogoUrl);

  win.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Resultados NOM-035 — ${escapeHtml(summary.title)}</title>
    </head>
    <body style="font-family: system-ui, -apple-system, sans-serif; padding: 2rem; max-width: 960px; margin: 0 auto;">
      ${logoHeader}
      <h1 style="margin: 0 0 0.35rem; font-size: 1.25rem; font-weight: 600;">Resultados NOM-035</h1>
      <p style="margin: 0 0 1.25rem; font-size: 0.9rem; color: #6b7280;">Vista de solo lectura de una campaña histórica · NOM-035-STPS-2018</p>

      <h2 style="font-size: 1.05rem; font-weight: 600; margin: 0 0 0.5rem;">${escapeHtml(summary.title)}</h2>
      <p style="margin: 0 0 1.25rem; font-size: 0.875rem; color: #4b5563;">
        Inicio: ${escapeHtml(inicio)} · Cierre: ${escapeHtml(cierre)} · Estado: <strong>${escapeHtml(summary.status)}</strong>
      </p>

      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin-bottom: 1.5rem;">
        <div style="padding: 1rem; background: #f9fafb; border-radius: 8px; border: 1px solid #e5e7eb;">
          <p style="margin: 0; font-size: 1.5rem; font-weight: 600;">${stats.totalCompleted}</p>
          <p style="margin: 0.35rem 0 0; font-size: 0.8rem; color: #6b7280;">Evaluaciones completadas</p>
        </div>
        <div style="padding: 1rem; background: #f9fafb; border-radius: 8px; border: 1px solid #e5e7eb;">
          <p style="margin: 0; font-size: 1.5rem; font-weight: 600;">${stats.highRiskG2G3}</p>
          <p style="margin: 0.35rem 0 0; font-size: 0.8rem; color: #6b7280;">Riesgo Alto/Muy Alto (G2/G3)</p>
        </div>
        <div style="padding: 1rem; background: #f9fafb; border-radius: 8px; border: 1px solid #e5e7eb;">
          <p style="margin: 0; font-size: 1.5rem; font-weight: 600;">${stats.clinicalG1}</p>
          <p style="margin: 0.35rem 0 0; font-size: 0.8rem; color: #6b7280;">Atención clínica (Guía I)</p>
        </div>
      </div>

      <h2 style="font-size: 1rem; font-weight: 600; margin: 1rem 0 0.5rem;">Resultados individuales</h2>
      <p style="font-size: 0.8rem; color: #6b7280; margin: 0 0 0.75rem;">Evaluaciones con estado completado en esta campaña.</p>
      <table style="width: 100%; border-collapse: collapse; font-size: 0.875rem;">
        <thead>
          <tr style="background: #f3f4f6;">
            <th style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb; text-align: left;">Empleado</th>
            <th style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb; text-align: left;">Fecha</th>
            <th style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb; text-align: left;">Guía</th>
            <th style="padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb; text-align: left;">Resultado</th>
          </tr>
        </thead>
        <tbody>
          ${rows || '<tr><td colspan="4" style="padding: 1rem; text-align: center; color: #6b7280;">No hay evaluaciones completadas en esta campaña.</td></tr>'}
        </tbody>
      </table>

      <p style="font-size: 0.8rem; color: #9ca3af; margin-top: 2rem;">
        Documento generado para cumplimiento de la NOM-035-STPS-2018.
      </p>
      <p style="font-size: 0.75rem; color: #9ca3af;">
        Generado por PsyTalent · ${escapeHtml(generatedAt)} (Ciudad de México)
      </p>
    </body>
    </html>
  `);
  win.document.close();
  win.focus();
  win.print();
  win.onafterprint = () => win.close();
}
